import { format, parseISO } from 'date-fns';
import type { ReactNode } from 'react';
import { Box, Column, DataRow, Divider, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { OutlineCheckCircleIcon } from '@tempmee/react-icons';
import { JobPostingStatus, JobPostingStatusMapper } from '~/domain/PermanentJobs';
import { EM_DASH } from '~/utils/String';
import { tryCatch } from '~/utils/TryCatch';
import { POSTING_DETAILS_COPY } from '../postingDetailsRules';

interface IJobOverviewCardProps {
  title: string;
  /** Display name, e.g. "Dental Hygienist". */
  profession: string;
  /** ISO timestamp the posting went live. */
  publishedAt?: string;
  officeName: string;
  status: JobPostingStatus;
}

const COPY = POSTING_DETAILS_COPY.jobOverview;

const formatPostedDate = (publishedAt?: string): string => {
  if (!publishedAt) return EM_DASH;
  const result = tryCatch(() => format(parseISO(publishedAt), 'MM/dd/yy'));
  return result.ok ? result.data : EM_DASH;
};

/**
 * An overline label over an h6-scale value — the Figma's `Atom/LabelStack`. `aside`
 * sits at the end of the label's line (the posting status, on the Title stack).
 */
const LabelStack = ({
  label,
  aside,
  children,
}: {
  label: string;
  aside?: ReactNode;
  children: ReactNode;
}) => (
  <Column gap={0} className="min-w-0">
    <Row align="center" justify="between" gap={2}>
      <Typography variant="overline" as="p" className="text-text-primary">
        {label}
      </Typography>
      {aside}
    </Row>
    <Typography variant="h6" as="p" numberOfLines={1} className="text-text-primary">
      {children}
    </Typography>
  </Column>
);

const PostingStatus = ({ status }: { status: JobPostingStatus }) => {
  const label = JobPostingStatusMapper.toHumanReadable(status);

  if (status !== JobPostingStatus.Active) {
    return (
      <Typography variant="body2" as="span" className="shrink-0 text-text-secondary">
        {label}
      </Typography>
    );
  }

  return (
    <Row align="center" gap={1} className="shrink-0">
      <OutlineCheckCircleIcon size={16} color={colors.primary.DEFAULT} />
      <Typography variant="body2" as="span" className="text-primary">
        {label}
      </Typography>
    </Row>
  );
};

/**
 * The posting's summary at the head of the Posting Details sidebar — the Figma's
 * `Molecule/LargeJobDetails`, which has no design-system block, composed from DS
 * primitives on the same 8px-radius, 12px-padding surface the sidebar blocks use.
 */
export const JobOverviewCard = ({
  title,
  profession,
  publishedAt,
  officeName,
  status,
}: IJobOverviewCardProps) => (
  <Row
    gap={3}
    p={3}
    className="w-full rounded-lg bg-background"
    data-cy="posting-job-overview"
    data-testid="posting-job-overview"
  >
    {/* The Figma's 2px indicator line; no DS primitive draws a bare accent rule. */}
    <Box width="2px" aria-hidden className="self-stretch shrink-0 rounded-full bg-primary" />
    <Column gap={2} className="min-w-0 flex-1">
      {/* Figma: the status sits top-right on the Title label's line, so the dividers
          below run the card's full content width. */}
      <LabelStack label={COPY.title} aside={<PostingStatus status={status} />}>
        {title || EM_DASH}
      </LabelStack>
      <Divider />
      <Row gap={4}>
        <Box className="min-w-0 flex-1">
          <LabelStack label={COPY.profession}>{profession || EM_DASH}</LabelStack>
        </Box>
        <Divider orientation="vertical" flexItem />
        <LabelStack label={COPY.posted}>{formatPostedDate(publishedAt)}</LabelStack>
      </Row>
      <Divider />
      <DataRow
        orientation="stacked"
        divider={false}
        label={COPY.location}
        value={officeName || EM_DASH}
      />
    </Column>
  </Row>
);
